import { motion } from "framer-motion";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Grain } from "../components/Grain";
import { FloatingWhatsApp } from "../components/FloatingWhatsApp";
import { ProductCard } from "../components/ProductCard";
import { byCategory, CATEGORIES } from "../data/products";
import { copy } from "../data/copy";

const CategoryPage = () => {
    const { cat } = useParams();
    const { lang, t } = useLang();

    if (!CATEGORIES.includes(cat)) {
        return <Navigate to="/shop" replace />;
    }

    const list = byCategory(cat);
    const meta = copy.categoryTiles[cat];

    return (
        <div className="min-h-screen bg-[color:var(--kp-cream)]" data-testid={`category-page-${cat}`}>
            <Grain />
            <Header />
            <main className="pt-28 md:pt-36 pb-24">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <Link
                        to="/shop"
                        className="inline-flex items-center gap-2 text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60 hover:text-[color:var(--kp-red)] mb-6"
                        data-testid="category-back-shop"
                    >
                        <ArrowLeft size={12} /> {t({ en: "All pickles", te: "అన్ని ఊరగాయలు" })}
                    </Link>

                    <div className="grid grid-cols-12 gap-4 md:gap-8 mb-14 md:mb-20 items-end">
                        <div className="col-span-12 md:col-span-5">
                            <div className="flex items-center gap-3 mb-4">
                                <span className="w-8 h-px bg-[color:var(--kp-red)]" />
                                <span
                                    className={`text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60 ${
                                        lang === "te" ? "font-telugu-body" : ""
                                    }`}
                                >
                                    {list.length} {t({ en: "pouches in this shelf", te: "ప్యాకెట్లు" })}
                                </span>
                            </div>
                            <p
                                className={`text-lg text-[color:var(--kp-ink)]/75 leading-relaxed max-w-md ${
                                    lang === "te" ? "font-telugu-body" : ""
                                }`}
                                data-testid="category-intro"
                            >
                                {t(meta.body)}
                            </p>
                        </div>
                        <div className="col-span-12 md:col-span-7">
                            <motion.h1
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
                                className={`text-[13vw] md:text-[9vw] leading-[0.92] tracking-editorial ${
                                    lang === "en" ? "font-serif-display" : "font-telugu-display"
                                }`}
                                data-testid="category-title"
                            >
                                {t(meta.title)}{" "}
                                <em
                                    className={`text-[color:var(--kp-red)] ${
                                        lang === "en" ? "italic" : "not-italic"
                                    }`}
                                >
                                    {t({ en: "pickles.", te: "ఊరగాయలు." })}
                                </em>
                            </motion.h1>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16 md:gap-y-24" data-testid="category-grid">
                        {list.map((p, i) => (
                            <ProductCard key={p.id} product={p} index={i} />
                        ))}
                    </div>
                </div>
            </main>
            <Footer />
            <FloatingWhatsApp />
        </div>
    );
};

export default CategoryPage;
